import { useRef, useEffect, RefObject } from 'react';

interface IOutsideClickConfig {
  callback: () => void;
  refs?: Array<RefObject<HTMLElement>>;
  enabled?: boolean;
}

const useOutsideClick = (input: (() => void) | IOutsideClickConfig): RefObject<HTMLDivElement> => {
  const rootRef = useRef<HTMLDivElement>(null);
  const config = typeof input === 'function' ? { callback: input, refs: [], enabled: true } : input;
  const { callback, refs = [], enabled = true } = config;

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const extraRefsRef = useRef(refs);
  extraRefsRef.current = refs;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      const insideRefs: Array<RefObject<HTMLElement>> = [rootRef as RefObject<HTMLElement>, ...extraRefsRef.current];
      const clickedInside = insideRefs.some((insideRef) => insideRef.current?.contains(target));
      if (!clickedInside) {
        callbackRef.current();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [enabled]);

  return rootRef;
};

export default useOutsideClick;
